import React from 'react';
import { Box, Typography, Paper, Grid } from '@mui/material';

// Define proper types instead of any
interface BinderDesignParams {
  design_mode?: string;
  binder_design?: boolean;
  binding_chain?: string;
  binding_region?: string;
  hotspots?: string[];
  binder_len_min?: number;
  binder_len_max?: number;
  model_type?: string;
  noise_scale?: number;
}

interface RFDiffusionBinderResultsProps {
  results: Record<string, unknown>;
}

/**
 * Component to display RFDiffusion binder design results
 */
export const RFDiffusionBinderResults: React.FC<RFDiffusionBinderResultsProps> = ({ 
  results 
}) => {
  // Extract binder design parameters
  const params = results.params as BinderDesignParams || {};
  const pdbFiles = results.pdb_files as string[] || [];
  
  // Render binding parameters section
  const renderBindingParameters = () => {
    return (
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Binding Parameters
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2">Binding Chain</Typography>
            <Typography>{params.binding_chain || 'Not specified'}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2">Binding Region</Typography>
            <Typography>{params.binding_region || 'Not specified'}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2">Hotspots</Typography>
            <Typography>
              {params.hotspots?.length 
                ? params.hotspots.join(', ') 
                : 'None specified'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2">Binder Length Range</Typography>
            <Typography>
              {params.binder_len_min && params.binder_len_max 
                ? `${params.binder_len_min} - ${params.binder_len_max}` 
                : 'Not specified'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2">Model Type</Typography>
            <Typography>{params.model_type || 'Default'}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2">Noise Scale</Typography>
            <Typography>{params.noise_scale || 'Default'}</Typography>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  // Render binding quality metrics section
  const renderBindingQualityMetrics = () => {
    return (
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Binding Quality Metrics
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Binding quality metrics will be available in a future update. These will include:
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2">• Binding interface area</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2">• Shape complementarity</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2">• Binding energy score</Typography>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h5" gutterBottom>
        RFDiffusion Binder Design Results
      </Typography>
      
      {renderBindingParameters()}
      {renderBindingQualityMetrics()}
      
      {/* Generated PDB files */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Generated PDB Files
        </Typography>
        {pdbFiles.length > 0 ? (
          pdbFiles.map((file, index) => (
            <Typography key={index} variant="body1">
              {file}
            </Typography>
          ))
        ) : (
          <Typography variant="body1">No PDB files generated</Typography>
        )}
      </Paper>
    </Box>
  );
}; 